import type { CanonicalBatchError, CanonicalPayloadBase } from './types';

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

function parseModelName(value: unknown): string | null {
  if (value === null) return null;
  if (typeof value !== 'string' || !value.trim()) {
    throw new Error('AI Session payload.modelName 必须是非空字符串或 null');
  }
  return value.trim();
}

function parseStageModelNames(value: unknown): Readonly<Record<string, string>> {
  if (!isRecord(value)) {
    throw new Error('AI Session payload.stageModelNames 必须是对象');
  }
  return Object.fromEntries(
    Object.entries(value).map(([stage, modelName]) => {
      if (!stage.trim() || typeof modelName !== 'string' || !modelName.trim()) {
        throw new Error('AI Session payload.stageModelNames 必须映射非空阶段名和模型名');
      }
      return [stage, modelName.trim()];
    })
  );
}

function parseBatchErrors(value: unknown): readonly CanonicalBatchError[] {
  if (!Array.isArray(value)) {
    throw new Error('AI Session payload.batchErrors 必须是数组');
  }
  return value.map((item, index) => {
    if (!isRecord(item)) {
      throw new Error(`AI Session payload.batchErrors[${index}] 必须是对象`);
    }
    const hasBatchIndex = Object.prototype.hasOwnProperty.call(item, 'batchIndex');
    const hasMessage = Object.prototype.hasOwnProperty.call(item, 'message');
    if (!hasBatchIndex && !hasMessage) {
      throw new Error(`AI Session payload.batchErrors[${index}] 缺少 batchIndex 或 message`);
    }
    if (
      hasBatchIndex &&
      (typeof item.batchIndex !== 'number' ||
        !Number.isInteger(item.batchIndex) ||
        item.batchIndex < 0)
    ) {
      throw new Error(`AI Session payload.batchErrors[${index}].batchIndex 必须是非负整数`);
    }
    if (hasMessage && (typeof item.message !== 'string' || !item.message.trim())) {
      throw new Error(`AI Session payload.batchErrors[${index}].message 必须是非空字符串`);
    }
    return {
      ...(hasBatchIndex ? { batchIndex: item.batchIndex as number } : {}),
      ...(hasMessage ? { message: (item.message as string).trim() } : {}),
    };
  });
}

export function parseCanonicalPayloadBase(payload: Record<string, unknown>): CanonicalPayloadBase {
  if (Object.prototype.hasOwnProperty.call(payload, 'error') && typeof payload.error !== 'string') {
    throw new Error('AI Session payload.error 必须是字符串');
  }
  if (
    Object.prototype.hasOwnProperty.call(payload, 'degraded') &&
    typeof payload.degraded !== 'boolean'
  ) {
    throw new Error('AI Session payload.degraded 必须是布尔值');
  }
  return {
    ...(typeof payload.error === 'string' ? { error: payload.error } : {}),
    ...(Object.prototype.hasOwnProperty.call(payload, 'batchErrors')
      ? { batchErrors: parseBatchErrors(payload.batchErrors) }
      : {}),
    ...(typeof payload.degraded === 'boolean' ? { degraded: payload.degraded } : {}),
    ...(Object.prototype.hasOwnProperty.call(payload, 'modelName')
      ? { modelName: parseModelName(payload.modelName) }
      : {}),
    ...(Object.prototype.hasOwnProperty.call(payload, 'stageModelNames')
      ? { stageModelNames: parseStageModelNames(payload.stageModelNames) }
      : {}),
    ...(isRecord(payload.lastAgentProgress)
      ? { lastAgentProgress: payload.lastAgentProgress }
      : {}),
  };
}
